"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CERTFIQUE_MENU_AJUDA,
  CERTFIQUE_MENU_CONTEUDO,
  CERTFIQUE_MENU_SOLUCOES,
  type CertfiqueNavItem
} from "@/components/certfique/certfique-header-menu";

const HOURS_DISMISS_KEY = "cf-hours-banner-dismissed";

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function GiftIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className="cf-nav-link">
      {children}
      <span className="cf-nav-link-underline" aria-hidden="true" />
    </a>
  );
}

function NavDropdown({ label, items }: { label: string; items: CertfiqueNavItem[] }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMenu = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  return (
    <div
      className={`cf-nav-dd cf-nav-dd-cert ${open ? "is-open" : ""}`}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onFocusCapture={openMenu}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) scheduleClose();
      }}
    >
      <button
        type="button"
        className="cf-nav-dd-trigger cf-nav-link cf-nav-dd-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <ChevronDown className="cf-nav-chevron" />
        <span className="cf-nav-link-underline" aria-hidden="true" />
      </button>
      <div className="cf-nav-dd-panel cf-nav-dd-panel-cert" role="menu">
        {items.map((item) =>
          item.external ? (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              {item.label}
            </a>
          ) : (
            <a key={item.label} href={item.href} role="menuitem" onClick={() => setOpen(false)}>
              {item.label}
            </a>
          )
        )}
      </div>
    </div>
  );
}

export function CertfiqueTopMenu({
  buyHref,
  buyLabel,
  whatsappHref
}: {
  buyHref: string;
  buyLabel: string;
  whatsappHref: string;
}) {
  const [hoursVisible, setHoursVisible] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(HOURS_DISMISS_KEY) === "1") setHoursVisible(false);
    } catch {
      /* ignore */
    }
  }, []);

  const dismissHours = useCallback(() => {
    setHoursVisible(false);
    try {
      localStorage.setItem(HOURS_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--cf-hours-h", hoursVisible ? "52px" : "0px");
    return () => {
      document.documentElement.style.removeProperty("--cf-hours-h");
    };
  }, [hoursVisible]);

  return (
    <>
      {hoursVisible ? (
        <div className="cf-hours-bar" role="region" aria-label="Horário de atendimento">
          <div className="cf-hours-bar-inner">
            <div className="cf-hours-bar-main">
              <ClockIcon />
              <span>Atendimento todos os dias, das 08h00 às 22h00</span>
              <span className="cf-hours-badge">A primeira certificadora com horário estendido</span>
            </div>
            <button type="button" className="cf-hours-close" onClick={dismissHours} aria-label="Fechar aviso">
              ×
            </button>
          </div>
        </div>
      ) : null}

      <header className="cf-header-cert">
        <div className="cf-container cf-header-cert-inner">
          <Link href="/" className="cf-header-cert-logo">
            <Image
              src="/logo_SP.png"
              alt="SP Certificado Digital"
              width={384}
              height={148}
              priority
              className="cf-header-sp-logo"
            />
          </Link>

          <nav className="cf-nav-cert" aria-label="Menu principal">
            <NavDropdown label="Soluções" items={CERTFIQUE_MENU_SOLUCOES} />
            <NavDropdown label="Conteúdo" items={CERTFIQUE_MENU_CONTEUDO} />
            <NavDropdown
              label="Ajuda"
              items={CERTFIQUE_MENU_AJUDA.map((item) =>
                item.label === "Fale no WhatsApp" ? { ...item, href: whatsappHref, external: true } : item
              )}
            />
            <a href="#contato" className="cf-nav-link cf-nav-indique-cert">
              <GiftIcon />
              Indique e ganhe
              <span className="cf-nav-link-underline" aria-hidden="true" />
            </a>
          </nav>

          <div className="cf-header-cert-actions">
            <Link href={buyHref} className="cf-header-brutal cf-header-brutal-primary cf-header-buy" title={`Comprar e-CNPJ A1 — ${buyLabel}`}>
              Comprar — {buyLabel}
            </Link>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="cf-header-brutal cf-header-brutal-ghost"
              title="Falar no WhatsApp"
            >
              <svg className="cf-wa-icon" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span className="cf-header-btn-text">WhatsApp</span>
            </a>
          </div>

          <button
            type="button"
            className="cf-header-mobile-toggle"
            aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            )}
          </button>
        </div>

        {mobileOpen ? (
          <div className="cf-mobile-drawer">
            <p className="cf-mobile-drawer-label">Soluções</p>
            {CERTFIQUE_MENU_SOLUCOES.map((item) => (
              <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)}>
                {item.label}
              </a>
            ))}
            <p className="cf-mobile-drawer-label">Conteúdo</p>
            {CERTFIQUE_MENU_CONTEUDO.map((item) => (
              <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)}>
                {item.label}
              </a>
            ))}
            <p className="cf-mobile-drawer-label">Ajuda</p>
            {CERTFIQUE_MENU_AJUDA.map((item) => (
              <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)}>
                {item.label}
              </a>
            ))}
            <a href="#contato" className="cf-nav-indique-cert" onClick={() => setMobileOpen(false)}>
              Indique e ganhe
            </a>
            <Link href={buyHref} className="cf-header-brutal cf-header-brutal-primary" onClick={() => setMobileOpen(false)}>
              Comprar — {buyLabel}
            </Link>
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="cf-header-brutal cf-header-brutal-ghost">
              WhatsApp
            </a>
          </div>
        ) : null}
      </header>
    </>
  );
}
